import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  Form, Input, Select, Switch, Button, Card, Row, Col,
  Typography, InputNumber, Collapse, Table, Divider,
  Upload, Modal, Checkbox, Space, Tooltip, message, Tag,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, ThunderboltOutlined,
  AppstoreOutlined, PictureOutlined, DollarOutlined,
  DatabaseOutlined, TagsOutlined, SettingOutlined,
  BarcodeOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import WholesaleProductEngine from '../../components/wholesale/WholesaleProductEngine';
import './ProductList.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

type Category    = { id: string; name: string };
type Supplier    = { id: string; name: string };
type ProductType = 'Physical' | 'Service' | 'Digital' | 'Variant';
interface VariantRow { key: string; name: string; sku: string; price: string; stock: string }

const UNITS = ['pcs', 'kg', 'g', 'l', 'ml', 'm', 'box', 'pack', 'dozen', 'pair', 'set'];
const PRODUCT_TYPES: { value: ProductType; icon: string; desc: string }[] = [
  { value: 'Physical', icon: '📦', desc: 'Physical goods with inventory' },
  { value: 'Service',  icon: '🛠️', desc: 'Services, labour, consulting'  },
  { value: 'Digital',  icon: '💾', desc: 'Downloadable files, software'  },
  { value: 'Variant',  icon: '🎨', desc: 'Multiple sizes, colours, etc.'  },
];

const LS = {
  get:     (k: string)             => { try { return localStorage.getItem(`pf_${k}`) ?? ''; } catch { return ''; } },
  set:     (k: string, v: string)  => { try { localStorage.setItem(`pf_${k}`, v); } catch {} },
  getJSON: <T,>(k: string, fb: T): T => { try { const v = localStorage.getItem(`pf_${k}`); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  setJSON: (k: string, v: unknown) => { try { localStorage.setItem(`pf_${k}`, JSON.stringify(v)); } catch {} },
};

const BLUE   = '#1890ff';
const GREEN  = '#10b981';
const GREY   = '#6b7280';
const PURPLE = '#8b5cf6';

const cardStyle: React.CSSProperties = {
  borderRadius: 12, marginBottom: 16, border: '1px solid #e5e7eb', boxShadow: 'none',
};
const sectionHead: React.CSSProperties = {
  fontSize: 15, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 8,
};
const btnBase: React.CSSProperties = {
  padding: '0 28px', height: 42, borderRadius: 8, fontWeight: 600, fontSize: 14,
};
const calcBoxStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)',
  border: '1px solid #bae6fd', borderRadius: 10,
  padding: '14px 24px', display: 'flex', gap: 40, flexWrap: 'wrap', marginTop: 12,
};

// Live QR code preview — reads SKU and name from the Ant Design form instance
const QrPreview: React.FC<{ form: any }> = ({ form }) => {
  const sku  = Form.useWatch('sku',  form) || '';
  const name = Form.useWatch('name', form) || '';
  if (!sku && !name) return (
    <div style={{ color: '#9ca3af', fontSize: 13 }}>
      Fill in the product name and SKU above — a QR code will appear here automatically.
    </div>
  );
  const payload = JSON.stringify({ sku: sku || '—', name: name || '—' });
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ background: '#fff', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, display: 'inline-block' }} className="product-qr-svg">
        <QRCodeSVG value={payload} size={140} level="M" includeMargin />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 8 }}>
          This QR code is generated from your product's SKU and name. It will be available to print or download after saving.
        </div>
        <div style={{ fontFamily: 'monospace', fontSize: 12, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', wordBreak: 'break-all' }}>
          {payload}
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <Button
            size="small"
            onClick={() => {
              const svg = document.querySelector('.product-qr-svg svg') as SVGElement | null;
              if (!svg) return;
              const blob = new Blob([svg.outerHTML], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `qr-${sku || 'product'}.svg`; a.click();
              URL.revokeObjectURL(url);
            }}
          >
            ⬇ Download SVG
          </Button>
          <Button size="small" onClick={() => window.print()}>🖨 Print</Button>
        </div>
      </div>
    </div>
  );
};

const ProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [form] = Form.useForm();

  const [quickMode,   setQuickMode]   = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [skuChecking, setSkuChecking] = useState(false);
  const [skuError,    setSkuError]    = useState('');

  const [showCatModal,   setShowCatModal]   = useState(false);
  const [showSupModal,   setShowSupModal]   = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [newCatName,     setNewCatName]     = useState('');
  const [newSupName,     setNewSupName]     = useState('');
  const [newBrandName,   setNewBrandName]   = useState('');

  const [categories, setCategories] = useState<Category[]>([]);
  const [suppliers,  setSuppliers]  = useState<Supplier[]>([]);
  const [brands,     setBrands]     = useState<string[]>(() => LS.getJSON('brands', []));

  const [productType,    setProductType]    = useState<ProductType>('Physical');
  const [trackInventory, setTrackInventory] = useState(true);
  const [lowStockAlert,  setLowStockAlert]  = useState(false);

  const [imagePreviews, setImagePreviews] = useState<(string | null)[]>([null, null, null]);
  const [imageFiles,    setImageFiles]    = useState<(File   | null)[]>([null, null, null]);
  const [digitalFile,   setDigitalFile]   = useState<File | null>(null);

  const [variants,     setVariants]     = useState<VariantRow[]>([]);
  const [variantAttrs, setVariantAttrs] = useState<string[]>(['Size', 'Colour']);

  const [calc, setCalc] = useState({ profit: 0, margin: 0, markup: 0 });
  const [isPublicShop, setIsPublicShop] = useState(true);

  const wholesalePriceWatch = Form.useWatch('wholesalePrice', form);
  const boxQuantityWatch    = Form.useWatch('boxQuantity', form);
  const boxesPerPalletWatch = Form.useWatch('boxesPerPallet', form);
  const categoryWatch       = Form.useWatch('category', form);
  const palletUnitPrice = Number(wholesalePriceWatch) > 0
    ? Number(wholesalePriceWatch)
    : Number(form.getFieldValue('price') || 0);
  const palletPrice = palletUnitPrice > 0 && Number(boxQuantityWatch) > 0 && Number(boxesPerPalletWatch) > 0
    ? palletUnitPrice * Number(boxQuantityWatch) * Number(boxesPerPalletWatch)
    : null;

  /* ─── live profit / margin / markup ─── */
  const recalc = useCallback(() => {
    const cost  = Number(form.getFieldValue('cost'))  || 0;
    const price = Number(form.getFieldValue('price')) || 0;
    const profit = price - cost;
    setCalc({
      profit,
      margin: price > 0 ? (profit / price) * 100 : 0,
      markup: cost  > 0 ? (profit / cost)  * 100 : 0,
    });
  }, [form]);

  /* ─── load data + smart defaults ─── */
  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    const h = { Authorization: `Bearer ${token}` };

    apiFetch(`${API_URL}/categories`, { headers: h })
      .then(r => r.json())
      .then((d: any) => {
        const arr = Array.isArray(d) ? d : d.categories ?? [];
        setCategories(arr.map((c: any) => ({ id: c._id || c.id, name: c.name })));
      }).catch(() => {});

    apiFetch(`${API_URL}/suppliers`, { headers: h })
      .then(r => r.json())
      .then((d: any) => setSuppliers((Array.isArray(d) ? d : []).map((s: any) => ({ id: s._id || s.id, name: s.name }))))
      .catch(() => {});

    const lastCat = LS.get('lastCategory');
    const prefix  = lastCat?.slice(0, 4).toUpperCase() || 'PROD';
    form.setFieldsValue({
      sku:          `${prefix}-${Math.floor(10000 + Math.random() * 90000)}`,
      category:     lastCat   || undefined,
      supplier:     LS.get('lastSupplier') || undefined,
      unit:         LS.get('lastUnit')     || 'pcs',
      taxRate:      Number(LS.get('lastTaxRate')) || 20,
      reorderPoint: 10,
      status:       'Active',
    });
    setTimeout(recalc, 0);
  }, [token, navigate, form, recalc]);

  /* ─── SKU duplicate check ─── */
  const checkSku = async (sku: string) => {
    if (!sku || !token) return;
    setSkuChecking(true);
    try {
      const r = await apiFetch(`${API_URL}/products`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      const dup = Array.isArray(d) && d.some((p: any) => p.sku === sku);
      setSkuError(dup ? `SKU "${sku}" is already in use` : '');
    } catch { setSkuError(''); }
    setSkuChecking(false);
  };

  const generateSku = () => {
    const cat    = form.getFieldValue('category');
    const prefix = cat?.slice(0, 4).toUpperCase() || 'PROD';
    form.setFieldValue('sku', `${prefix}-${Math.floor(10000 + Math.random() * 90000)}`);
    setSkuError('');
  };

  /* ─── image helpers ─── */
  const compressImage = (file: File): Promise<string> =>
    new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const img = new Image();
        img.onload = () => {
          const MAX = 800;
          let w = img.width, h = img.height;
          if (w > MAX || h > MAX) {
            if (w > h) { h = Math.round((h * MAX) / w); w = MAX; }
            else       { w = Math.round((w * MAX) / h); h = MAX; }
          }
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d')!.drawImage(img, 0, 0, w, h);
          resolve(c.toDataURL('image/jpeg', 0.85));
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });

  const handleImageDrop = async (file: File, idx: number) => {
    if (!file.type.startsWith('image/')) { message.error('Only image files allowed'); return false; }
    let dataUrl: string;
    if (file.size > 2 * 1024 * 1024) {
      message.info('Compressing large image...');
      dataUrl = await compressImage(file);
    } else {
      dataUrl = await new Promise(r => {
        const fr = new FileReader();
        fr.onload = e => r(e.target?.result as string);
        fr.readAsDataURL(file);
      });
    }
    const p = [...imagePreviews]; p[idx] = dataUrl;
    const f = [...imageFiles];   f[idx] = file;
    setImagePreviews(p); setImageFiles(f);
    return false;
  };

  const removeImage = (idx: number) => {
    const p = [...imagePreviews]; p[idx] = null;
    const f = [...imageFiles];   f[idx] = null;
    setImagePreviews(p); setImageFiles(f);
  };

  /* ─── inline creators ─── */
  const createCategory = async () => {
    if (!newCatName.trim()) return;
    const r = await apiFetch(`${API_URL}/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ name: newCatName.trim(), type: 'product' }),
    });
    const d = await r.json();
    if (r.ok) {
      const nc = { id: d.category?.id || d.id || newCatName.trim(), name: newCatName.trim() };
      setCategories(p => [...p, nc]);
      form.setFieldValue('category', nc.name);
      setNewCatName(''); setShowCatModal(false);
      message.success('Category created');
    } else message.error(d.message || 'Failed to create category');
  };

  const createSupplier = async () => {
    if (!newSupName.trim()) return;
    const name = newSupName.trim();
    const r = await apiFetch(`${API_URL}/suppliers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        name,
        company: name,
        category: 'Other',
        country: 'United Kingdom',
        status: 'Active',
        paymentTerms: 'Net 30',
      }),
    });
    const d = await r.json();
    if (r.ok) {
      const ns = { id: String(d.id || d._id), name };
      setSuppliers(p => [...p, ns]);
      form.setFieldValue('supplier', ns.id);
      setNewSupName(''); setShowSupModal(false);
      message.success('Supplier created — complete their profile in Suppliers when ready');
    } else message.error(d.message || d.error || 'Failed to create supplier');
  };

  const createBrand = () => {
    if (!newBrandName.trim()) return;
    const updated = [...brands, newBrandName.trim()];
    setBrands(updated); LS.setJSON('brands', updated);
    form.setFieldValue('brand', newBrandName.trim());
    setNewBrandName(''); setShowBrandModal(false);
  };

  /* ─── variant helpers ─── */
  const addVariant    = () => setVariants(p => [...p, { key: Date.now().toString(), name: '', sku: '', price: '', stock: '' }]);
  const updateVariant = (key: string, field: keyof VariantRow, val: string) =>
    setVariants(p => p.map(v => v.key === key ? { ...v, [field]: val } : v));
  const removeVariant = (key: string) => setVariants(p => p.filter(v => v.key !== key));

  /* ─── submit ─── */
  const appendProductFields = (fd: FormData, flat: Record<string, string>) => {
    const alwaysSend = new Set([
      'name', 'price', 'cost', 'stock', 'status', 'taxRate', 'unit', 'minStock',
      'productType', 'trackInventory', 'lowStockAlert', 'isPublicShop', 'tags', 'variants',
    ]);
    Object.entries(flat).forEach(([k, v]) => {
      if (alwaysSend.has(k) || (v !== '' && v !== 'undefined')) fd.append(k, v);
    });
  };

  const doSubmit = async (values: any, statusOverride?: string) => {
    if (skuError) { message.error('Fix the SKU error before saving'); return; }
    statusOverride === 'Draft' ? setSavingDraft(true) : setSaving(true);
    try {
      const finalStatus = statusOverride || values.status || 'Active';
      if (values.category) LS.set('lastCategory', values.category);
      if (values.supplier)  LS.set('lastSupplier',  values.supplier);
      if (values.unit)      LS.set('lastUnit',      values.unit);
      if (values.taxRate)   LS.set('lastTaxRate',   String(values.taxRate));

      const stockValue = (productType === 'Service' || productType === 'Digital' || !trackInventory)
        ? 0
        : Number(values.quantity ?? 0);

      const fd = new FormData();
      const flat: Record<string, string> = {
        name: values.name || '', description: values.description || '',
        sku: values.sku || '', category: values.category || '',
        supplier: values.supplier || '', brand: values.brand || '',
        price: String(values.price ?? ''), cost: String(values.cost ?? ''),
        taxRate: String(values.taxRate ?? 20),
        stock: String(stockValue), unit: values.unit || 'pcs',
        minStock: String(values.reorderPoint ?? 10),
        barcode: values.barcode || '', weight: values.weight || '',
        dimensions: values.dimensions || '', notes: values.notes || '',
        status: finalStatus, productType,
        trackInventory: String(trackInventory), lowStockAlert: String(lowStockAlert),
        tags: Array.isArray(values.tags) ? values.tags.join(',') : '',
        variants: JSON.stringify(variants),
        wholesalePrice: values.wholesalePrice != null && values.wholesalePrice !== '' ? String(values.wholesalePrice) : '',
        boxQuantity: values.boxQuantity != null && values.boxQuantity !== '' ? String(values.boxQuantity) : '',
        minOrderQty: values.minOrderQty != null && values.minOrderQty !== '' ? String(values.minOrderQty) : '',
        boxesPallet: values.boxesPerPallet != null && values.boxesPerPallet !== '' ? String(values.boxesPerPallet) : '',
        caseQuantity: values.caseQuantity != null && values.caseQuantity !== '' ? String(values.caseQuantity) : (values.boxQuantity != null ? String(values.boxQuantity) : ''),
        casePrice: values.casePrice != null && values.casePrice !== '' ? String(values.casePrice) : '',
        tierAPrice: values.tierAPrice != null && values.tierAPrice !== '' ? String(values.tierAPrice) : '',
        tierBPrice: values.tierBPrice != null && values.tierBPrice !== '' ? String(values.tierBPrice) : '',
        tierCPrice: values.tierCPrice != null && values.tierCPrice !== '' ? String(values.tierCPrice) : '',
        uomUnits: values.uomUnits || form.getFieldValue('uomUnits') || '[]',
        netWeight: values.netWeight || '',
        grossWeight: values.grossWeight || '',
        countryOfOrigin: values.countryOfOrigin || '',
        isPublicShop: String(isPublicShop),
        expiryDate: values.expiryDate || '',
        ingredients: values.ingredients || '',
        allergens: Array.isArray(values.allergens) ? values.allergens.join(',') : (values.allergens || ''),
        dietaryInfo: Array.isArray(values.dietaryInfo) ? values.dietaryInfo.join(',') : (values.dietaryInfo || ''),
      };
      appendProductFields(fd, flat);
      imageFiles.forEach((f, i) => { if (f) fd.append(`image${i + 1}`, f); });
      if (digitalFile) fd.append('digitalFile', digitalFile);

      const r = await apiFetch(`${API_URL}/products`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.ok) {
        message.success(finalStatus === 'Draft' ? 'Saved as draft!' : 'Product created!');
        navigate('/products/list', { state: { flash: `Product ${finalStatus === 'Draft' ? 'saved as draft' : 'created'} successfully` } });
      } else {
        const e = await r.json().catch(() => ({}));
        message.error(e.message || 'Failed to create product');
      }
    } catch { message.error('Network error — check your connection'); }
    setSaving(false); setSavingDraft(false);
  };

  const handleFinish     = (values: any) => doSubmit(values);
  const handleDraft      = async () => { try { const v = await form.validateFields(['name']); doSubmit(v, 'Draft'); } catch {} };
  const handleAddAnother = async () => {
    try {
      const v = await form.validateFields();
      await doSubmit(v);
      form.resetFields(); generateSku();
      setImagePreviews([null, null, null]); setImageFiles([null, null, null]);
      setCalc({ profit: 0, margin: 0, markup: 0 }); setVariants([]); setDigitalFile(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {}
  };

  /* ─────────────────────────────  Quick Create Mode  ─────────────────────────────── */
  if (quickMode) {
    return (
      <div style={{ maxWidth: 520, margin: '48px auto', padding: '0 20px' }}>
        <Card
          title={<span><ThunderboltOutlined style={{ color: BLUE, marginRight: 8 }} />Quick Create</span>}
          extra={<Button size="small" onClick={() => setQuickMode(false)}>Full Form</Button>}
          style={cardStyle}
        >
          <Form form={form} layout="vertical" onFinish={handleFinish}>
            <Form.Item name="name" label={<b>Product Name</b>} rules={[{ required: true }]}>
              <Input size="large" placeholder="Product name" />
            </Form.Item>
            <Form.Item name="price" label={<b>Selling Price</b>} rules={[{ required: true }]}>
              <InputNumber style={{ width: '100%' }} size="large" placeholder="0.00" min={0} step={0.01} addonAfter="GBP" />
            </Form.Item>
            <Form.Item name="category" label={<b>Category</b>}>
              <Select placeholder="Select category" allowClear size="large">
                {categories.map(c => <Option key={c.id} value={c.name}>{c.name}</Option>)}
              </Select>
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, paddingTop: 8 }}>
              <Button style={{ ...btnBase, background: GREY, borderColor: GREY, color: '#fff' }}
                onClick={() => navigate('/products/list')}>Cancel</Button>
              <Space>
                <Button onClick={() => setQuickMode(false)}>Full Form</Button>
                <Button type="primary" htmlType="submit" loading={saving}
                  style={{ ...btnBase, background: GREEN, borderColor: GREEN }}>Create Product</Button>
              </Space>
            </div>
          </Form>
        </Card>
      </div>
    );
  }

  /* ─────────────────────────────  Full Form  ─────────────────────────────── */
  return (
      <div style={{ maxWidth: 920, margin: '28px auto', padding: '0 20px' }} className="prod-list-page">

      <div className="prod-form-header">
        <div>
          <h1 className="prod-form-title">📦 Add New Product</h1>
          <p className="prod-form-subtitle">Name, price, stock, and category — expand sections below only when you need them</p>
        </div>
        <Space wrap>
          <Button icon={<ThunderboltOutlined />} onClick={() => setQuickMode(true)} size="small">Quick create</Button>
          <button type="button" className="prod-back-btn" onClick={() => navigate('/products/list')}>← Back</button>
        </Space>
      </div>

      <Form form={form} layout="vertical" onFinish={handleFinish}
        onValuesChange={(changed) => { if ('cost' in changed || 'price' in changed) recalc(); }}
        requiredMark={false}>

        {/* ── 1. Basic Identity ── */}
        <Card title={<span style={sectionHead}><TagsOutlined style={{ color: BLUE }} /> Basic Information</span>} style={cardStyle} className="prod-section-card">
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item name="name" label={<b>Product Name <span style={{ color: '#ef4444' }}>*</span></b>}
                rules={[{ required: true, message: 'Product name is required' }]}>
                <Input size="large" placeholder="e.g. Haircut, Olive Oil 1L, Plumbing Visit..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="status" label={<b>Status</b>} initialValue="Active">
                <Select size="large">
                  <Option value="Active"><Tag color="green">Active</Tag></Option>
                  <Option value="Draft"><Tag color="blue">Draft</Tag></Option>
                  <Option value="Inactive"><Tag color="default">Inactive</Tag></Option>
                  <Option value="Out of Stock"><Tag color="orange">Out of Stock</Tag></Option>
                  <Option value="Discontinued"><Tag color="red">Discontinued</Tag></Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="category" label={<b>Category</b>}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Form.Item name="category" noStyle>
                    <Select placeholder="Select category" allowClear style={{ flex: 1 }}>
                      {categories.map(c => <Option key={c.id} value={c.name}>{c.name}</Option>)}
                    </Select>
                  </Form.Item>
                  <Button icon={<PlusOutlined />} onClick={() => setShowCatModal(true)}
                    style={{ background: GREEN, borderColor: GREEN, color: '#fff' }}>New</Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label={<b>Description</b>}>
            <TextArea rows={3} placeholder="Features, ingredients, what's included..." />
          </Form.Item>
        </Card>

        {/* ── 2. Food Safety & Allergens ── */}
        <Card
          title={
            <span style={sectionHead}>
              <span style={{ fontSize: 16 }}>🛡️</span> Food Safety &amp; Allergens
              <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>(UK FIC Regulation — optional for non-food products)</span>
            </span>
          }
          style={cardStyle}
          className="prod-section-card"
        >
          <div style={{ padding: '10px 16px', background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 10, marginBottom: 20, fontSize: 13, color: '#92400e', fontWeight: 500 }}>
            ⚖️ UK Food Information Regulations require allergen declaration for all food products sold to consumers. This information will also be displayed on your product listings and can be exported.
          </div>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="expiryDate" label={<b>Expiry / Best Before Date</b>}>
                <input
                  type="date"
                  style={{ width: '100%', padding: '7px 12px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 14, outline: 'none', fontFamily: 'inherit' }}
                  onChange={e => form.setFieldValue('expiryDate', e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="allergens" label={<b>Contains Allergens <span style={{ fontWeight: 400, fontSize: 12, color: '#9ca3af' }}>(UK FIC 14 major allergens)</span></b>}>
            <Select mode="multiple" placeholder="Select all that apply…" size="large" allowClear
              style={{ width: '100%' }}
              options={[
                { value: 'Celery',               label: '🌿 Celery' },
                { value: 'Gluten (Wheat/Rye/Barley/Oats)', label: '🌾 Gluten (Wheat / Rye / Barley / Oats)' },
                { value: 'Crustaceans',          label: '🦐 Crustaceans (Prawns, Crabs, Lobster)' },
                { value: 'Eggs',                 label: '🥚 Eggs' },
                { value: 'Fish',                 label: '🐟 Fish' },
                { value: 'Lupin',                label: '🌸 Lupin' },
                { value: 'Milk',                 label: '🥛 Milk (including Lactose)' },
                { value: 'Molluscs',             label: '🦪 Molluscs (Clams, Mussels, Oysters, Squid)' },
                { value: 'Mustard',              label: '🌻 Mustard' },
                { value: 'Peanuts',              label: '🥜 Peanuts' },
                { value: 'Sesame',               label: '🫙 Sesame Seeds' },
                { value: 'Soybeans',             label: '🫘 Soybeans' },
                { value: 'Sulphites',            label: '🍷 Sulphur Dioxide & Sulphites (>10mg/kg)' },
                { value: 'Tree Nuts',            label: '🌰 Tree Nuts (Almonds, Hazelnuts, Walnuts, Cashews, Pecans, Brazil, Pistachio, Macadamia)' },
              ]}
            />
          </Form.Item>
          <Form.Item name="dietaryInfo" label={<b>Dietary Suitability</b>}>
            <Select mode="multiple" placeholder="Select all that apply…" size="large" allowClear
              style={{ width: '100%' }}
              options={[
                { value: 'Vegan',        label: '🌱 Vegan' },
                { value: 'Vegetarian',   label: '🥦 Vegetarian' },
                { value: 'Gluten-Free',  label: '✅ Gluten-Free' },
                { value: 'Halal',        label: '☪️ Halal' },
                { value: 'Kosher',       label: '✡️ Kosher' },
                { value: 'Dairy-Free',   label: '🚫 Dairy-Free' },
                { value: 'Nut-Free',     label: '🚫 Nut-Free' },
                { value: 'Organic',      label: '🌿 Organic' },
              ]}
            />
          </Form.Item>
          <Form.Item name="ingredients" label={<b>Ingredients List</b>}>
            <TextArea rows={4} placeholder="e.g. Water, Sugar, Salt, Citric Acid (E330), Natural Flavourings..." />
          </Form.Item>
        </Card>

        {/* ── 3. Images ── */}
        <Card
          title={
            <span style={sectionHead}>
              <PictureOutlined style={{ color: BLUE }} /> Images
              <span style={{ fontSize: 12, fontWeight: 400, color: '#9ca3af' }}>(optional — up to 3, drag & drop or click)</span>
            </span>
          }
          style={cardStyle}
        >
          <Row gutter={16}>
            {[0, 1, 2].map(idx => (
              <Col xs={24} sm={8} key={idx}>
                {imagePreviews[idx] ? (
                  <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '2px solid #e5e7eb' }}>
                    <img src={imagePreviews[idx]!} alt={`product-${idx}`}
                      style={{ width: '100%', height: 170, objectFit: 'cover', display: 'block' }} />
                    <button type="button" onClick={() => removeImage(idx)} style={{
                      position: 'absolute', top: 7, right: 7,
                      background: '#ef4444', color: '#fff', border: 'none',
                      borderRadius: '50%', width: 26, height: 26, cursor: 'pointer',
                      fontWeight: 700, fontSize: 16, lineHeight: '26px',
                    }}>×</button>
                    {idx === 0 && (
                      <div style={{ position: 'absolute', bottom: 7, left: 7, background: BLUE, color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>MAIN</div>
                    )}
                  </div>
                ) : (
                  <Upload.Dragger accept="image/jpeg,image/png,image/gif,image/webp"
                    showUploadList={false} beforeUpload={file => { handleImageDrop(file, idx); return false; }}
                    style={{ borderRadius: 10, height: 170 }}>
                    <div style={{ padding: '24px 0' }}>
                      <p style={{ fontSize: 32, margin: 0 }}>+</p>
                      <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                        {idx === 0 ? 'Main Image' : `Image ${idx + 1}`}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9ca3af' }}>
                        JPG, PNG, GIF, WEBP · Max 2 MB
                      </p>
                    </div>
                  </Upload.Dragger>
                )}
              </Col>
            ))}
          </Row>
        </Card>

        {/* ── 3. Pricing ── */}
        <Card title={<span style={sectionHead}><DollarOutlined style={{ color: BLUE }} /> Pricing</span>} style={cardStyle}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="price" label={<b>Retail Price <span style={{ color: '#ef4444' }}>*</span></b>}
                rules={[{ required: true, message: 'Selling price is required' }, { type: 'number', min: 0 }]}>
                <InputNumber style={{ width: '100%' }} size="large" placeholder="0.00" min={0} step={0.01} precision={2} addonAfter="GBP" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cost" label={<b>Cost Price <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(for profit tracking)</span></b>}>
                <InputNumber style={{ width: '100%' }} size="large" placeholder="0.00" min={0} step={0.01} precision={2} addonAfter="GBP" />
              </Form.Item>
            </Col>
          </Row>
          {/* Live profit calc */}
          <div style={calcBoxStyle}>
            {[
              { label: 'Profit', value: `£${calc.profit.toFixed(2)}`, color: calc.profit >= 0 ? GREEN : '#ef4444' },
              { label: 'Margin', value: `${calc.margin.toFixed(1)}%`, color: calc.margin >= 0 ? GREEN : '#ef4444' },
              { label: 'Markup', value: `${calc.markup.toFixed(1)}%`, color: calc.markup >= 0 ? BLUE  : '#ef4444' },
            ].map(m => (
              <div key={m.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>{m.label}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: m.color, lineHeight: 1.2 }}>{m.value}</div>
              </div>
            ))}
            <div style={{ textAlign: 'center', flex: 1, alignSelf: 'center' }}>
              <Text type="secondary" style={{ fontSize: 11 }}>Updates live as you type</Text>
            </div>
          </div>

          {/* Wholesale / Bulk — optional, clean divider keeps it separate */}
          <Divider style={{ margin: '20px 0 16px' }}>
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
              Wholesale / Bulk Pricing&nbsp;
              <span style={{ fontWeight: 400, fontSize: 12 }}>(optional — leave blank if not needed)</span>
            </span>
          </Divider>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="wholesalePrice" label={<b>Wholesale Price <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(trade / bulk buyers, per unit)</span></b>}>
                <InputNumber style={{ width: '100%' }} placeholder="0.00" min={0} step={0.01} precision={2} addonAfter="GBP" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="boxQuantity" label={<b>Units per Box <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(e.g. 12, 24, 48)</span></b>}>
                <InputNumber style={{ width: '100%' }} placeholder="e.g. 12" min={1} step={1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="boxesPerPallet" label={<b>Boxes per Pallet <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(used to auto-calculate pallet price)</span></b>}>
                <InputNumber style={{ width: '100%' }} placeholder="e.g. 40" min={1} step={1} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="minOrderQty" label={<b>Min. Order Qty <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(wholesale MOQ)</span></b>}>
                <InputNumber style={{ width: '100%' }} min={1} placeholder="e.g. 6" />
              </Form.Item>
            </Col>
          </Row>
          {palletPrice != null && (
            <div style={{ ...calcBoxStyle, background: 'linear-gradient(135deg,#faf5ff,#f3e8ff)', borderColor: '#ddd6fe' }}>
              <div>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase' }}>Auto Pallet Price</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: PURPLE }}>
                  £{palletPrice.toFixed(2)}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {boxesPerPalletWatch} boxes × {boxQuantityWatch} units × £{palletUnitPrice.toFixed(2)} per unit
                </div>
              </div>
            </div>
          )}

          <WholesaleProductEngine form={form} category={categoryWatch} />
        </Card>

        {/* ── 4. Stock & Publishing ── */}
        <Card title={<span style={sectionHead}><DatabaseOutlined style={{ color: BLUE }} /> Stock & Publishing</span>} style={cardStyle}>

          {/* Product Type — now lives here, below pricing */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, marginBottom: 10, color: '#374151' }}>Product Type</div>
            <Row gutter={10}>
              {PRODUCT_TYPES.map(pt => (
                <Col xs={12} sm={6} key={pt.value}>
                  <div onClick={() => setProductType(pt.value)} style={{
                    border: productType === pt.value ? `2px solid ${BLUE}` : '2px solid #e5e7eb',
                    borderRadius: 8, padding: '10px 8px', textAlign: 'center', cursor: 'pointer',
                    background: productType === pt.value ? '#eff6ff' : '#fafafa', transition: 'all 0.15s',
                  }}>
                    <div style={{ fontSize: 22 }}>{pt.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: 12, marginTop: 2, color: productType === pt.value ? BLUE : '#374151' }}>{pt.value}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>{pt.desc}</div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>

          {/* Stock toggle + fields */}
          {productType !== 'Service' && productType !== 'Digital' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <Text style={{ fontSize: 13, color: '#6b7280' }}>Track Stock</Text>
              <Switch checked={trackInventory} onChange={setTrackInventory}
                style={trackInventory ? { background: GREEN } : {}} />
            </div>
          )}

          {productType !== 'Service' && productType !== 'Digital' && trackInventory && (
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="quantity" label={<b>Current Stock <span style={{ color: '#ef4444' }}>*</span></b>}
                  rules={[{ required: true, message: 'Quantity is required' }, { type: 'number', min: 0 }]}>
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="0" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="unit" label={<b>Unit</b>} initialValue="pcs">
                  <Select>{UNITS.map(u => <Option key={u} value={u}>{u}</Option>)}</Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="reorderPoint" label={<b>Low Stock Alert At</b>} initialValue={10}>
                  <InputNumber style={{ width: '100%' }} min={0} placeholder="10" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {productType !== 'Service' && productType !== 'Digital' && !trackInventory && (
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#9ca3af' }}>
              <DatabaseOutlined style={{ fontSize: 28 }} />
              <div style={{ marginTop: 6 }}><Text type="secondary">Stock tracking is off</Text></div>
            </div>
          )}

          {/* Variants */}
          {productType === 'Variant' && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 700 }}>Variant Options</div>
                <Button icon={<PlusOutlined />} size="small" onClick={addVariant}
                  style={{ background: BLUE, borderColor: BLUE, color: '#fff' }}>Add Variant</Button>
              </div>
              <div style={{ marginBottom: 10 }}>
                <Text style={{ fontWeight: 600, marginRight: 10 }}>Attributes:</Text>
                <Select mode="tags" value={variantAttrs} onChange={setVariantAttrs}
                  style={{ minWidth: 280 }} placeholder="Size, Colour, Material..." size="small" />
              </div>
              <Table size="small" dataSource={variants} rowKey="key" pagination={false}
                locale={{ emptyText: 'Click Add Variant to start' }}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}
                columns={[
                  { title: 'Variant Name', dataIndex: 'name', render: (v, r: VariantRow) => <Input value={v} size="small" placeholder="e.g. Red / Large" onChange={e => updateVariant(r.key, 'name', e.target.value)} /> },
                  { title: 'SKU',          dataIndex: 'sku',  render: (v, r: VariantRow) => <Input value={v} size="small" placeholder="SKU" onChange={e => updateVariant(r.key, 'sku', e.target.value)} /> },
                  { title: 'Price (£)',    dataIndex: 'price', render: (v, r: VariantRow) => <Input value={v} size="small" type="number" placeholder="0.00" onChange={e => updateVariant(r.key, 'price', e.target.value)} /> },
                  { title: 'Stock',        dataIndex: 'stock', render: (v, r: VariantRow) => <Input value={v} size="small" type="number" placeholder="0" onChange={e => updateVariant(r.key, 'stock', e.target.value)} /> },
                  { title: '', width: 44, render: (_, r: VariantRow) => <Button size="small" icon={<DeleteOutlined />} danger onClick={() => removeVariant(r.key)} /> },
                ]}
              />
            </div>
          )}

          {/* Digital file upload */}
          {productType === 'Digital' && (
            <div style={{ marginTop: 8 }}>
              <Upload.Dragger accept=".pdf,.zip,.docx,.mp4,.mp3,.jpg,.png"
                showUploadList={false} maxCount={1}
                beforeUpload={f => { setDigitalFile(f); return false; }} style={{ borderRadius: 10 }}>
                <div style={{ padding: '24px 0' }}>
                  <p style={{ fontSize: 40, margin: 0 }}>📁</p>
                  <p style={{ fontWeight: 600, margin: '8px 0 0' }}>Drag and drop your digital file here</p>
                  <p style={{ color: '#9ca3af', fontSize: 12, margin: '4px 0 0' }}>PDF · ZIP · DOCX · MP4 · MP3</p>
                </div>
              </Upload.Dragger>
              {digitalFile && (
                <div style={{ marginTop: 12, padding: '10px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text>{digitalFile.name} <Text type="secondary" style={{ fontSize: 12 }}>({(digitalFile.size / 1024).toFixed(0)} KB)</Text></Text>
                  <Button size="small" danger onClick={() => setDigitalFile(null)}>Remove</Button>
                </div>
              )}
            </div>
          )}

          {/* Online Shop toggle */}
          <div style={{ marginTop: 20, padding: '16px 20px', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #86efac', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 16 }}>
            <Switch
              checked={isPublicShop}
              onChange={setIsPublicShop}
              style={{ background: isPublicShop ? GREEN : undefined, flexShrink: 0 }}
            />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🛍️ Show in Online Shop</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {isPublicShop
                  ? 'Customers can browse and buy this product from your shop link.'
                  : 'This product is hidden from the public shop.'}
              </div>
            </div>
          </div>
        </Card>

        {/* ── 5. Advanced Settings (collapsed — for power users) ── */}
        <Collapse ghost style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', marginBottom: 20 }}>
          <Panel
            header={
              <span style={{ fontWeight: 600, color: GREY }}>
                ⚙️ Advanced Settings&nbsp;
                <span style={{ fontWeight: 400, fontSize: 12, color: '#9ca3af' }}>
                  — SKU, barcode, supplier, dimensions, min order qty...
                </span>
              </span>
            }
            key="advanced"
          >
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="sku"
                  label={<b>SKU <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>(auto-generated if blank)</span></b>}
                  validateStatus={skuError ? 'error' : skuChecking ? 'validating' : ''}
                  help={skuError || (skuChecking ? 'Checking uniqueness...' : '')}>
                  <Input placeholder="e.g. ELEC-48321"
                    onBlur={e => checkSku(e.target.value)}
                    addonAfter={
                      <Tooltip title="Auto-generate unique SKU">
                        <BarcodeOutlined onClick={generateSku} style={{ cursor: 'pointer', color: BLUE }} />
                      </Tooltip>
                    } />
                </Form.Item>
              </Col>
            </Row>
            {productType === 'Physical' && (
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="weight" label={<b>Shipping Weight (legacy)</b>}>
                    <Input placeholder="e.g. 500g, 2kg — use Net/Gross below for compliance" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="dimensions" label={<b>Dimensions (L × W × H)</b>}>
                    <Input placeholder="30×20×10 cm" />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="supplier" label={<b>Supplier</b>}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Form.Item name="supplier" noStyle>
                      <Select placeholder="Select supplier" allowClear style={{ flex: 1 }}>
                        {suppliers.map(s => <Option key={s.id} value={s.id}>{s.name}</Option>)}
                      </Select>
                    </Form.Item>
                    <Button icon={<PlusOutlined />} onClick={() => setShowSupModal(true)}
                      style={{ background: GREEN, borderColor: GREEN, color: '#fff' }}>New</Button>
                  </div>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="brand" label={<b>Brand / Manufacturer</b>}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Form.Item name="brand" noStyle>
                      <Select placeholder="Select brand" allowClear style={{ flex: 1 }}>
                        {brands.map(b => <Option key={b} value={b}>{b}</Option>)}
                      </Select>
                    </Form.Item>
                    <Button icon={<PlusOutlined />} onClick={() => setShowBrandModal(true)}
                      style={{ background: GREEN, borderColor: GREEN, color: '#fff' }}>New</Button>
                  </div>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="taxRate" label={<b>UK VAT Rate</b>} initialValue={20}>
                  <Select>
                    <Option value={20}>Standard — 20% (most goods)</Option>
                    <Option value={5}>Reduced — 5% (energy, children&apos;s car seats)</Option>
                    <Option value={0}>Zero — 0% (most food, books, children&apos;s clothes)</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="tags" label={<b>Tags</b>}>
                  <Select mode="tags" placeholder="Press Enter to add tags (e.g. new, sale, featured)" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="notes" label={<b>Internal Notes</b>}>
              <TextArea rows={3} placeholder="Storage instructions, supplier notes, internal references..." />
            </Form.Item>
            {/* QR Code preview lives in Advanced — power users only */}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#374151', display: 'flex', alignItems: 'center', gap: 8 }}>
                <BarcodeOutlined style={{ color: PURPLE }} /> QR Code Preview
              </div>
              <QrPreview form={form} />
            </div>
          </Panel>
        </Collapse>

        {/* ── Footer ── */}
        <Card style={{ ...cardStyle, background: '#f9fafb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <Button onClick={() => navigate('/products/list')}
              style={{ ...btnBase, background: GREY, borderColor: GREY, color: '#fff' }}>
              Cancel
            </Button>
            <Space wrap>
              <Button onClick={handleDraft} loading={savingDraft} style={btnBase}>
                Save Draft
              </Button>
              <Button onClick={handleAddAnother}
                style={{ ...btnBase, background: PURPLE, borderColor: PURPLE, color: '#fff' }}>
                Create &amp; Add Another
              </Button>
              <Button type="primary" htmlType="submit" loading={saving}
                style={{ ...btnBase, background: GREEN, borderColor: GREEN, fontSize: 15, fontWeight: 700, padding: '0 36px' }}>
                Create Product
              </Button>
            </Space>
          </div>
        </Card>

      </Form>

      {/* ── Modals ── */}
      <Modal title="New Category" open={showCatModal}
        onOk={createCategory} onCancel={() => { setShowCatModal(false); setNewCatName(''); }}
        okText="Create" okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}>
        <Input value={newCatName} onChange={e => setNewCatName(e.target.value)}
          placeholder="Category name" onPressEnter={createCategory} size="large" autoFocus />
      </Modal>

      <Modal title="New Supplier" open={showSupModal}
        onOk={createSupplier} onCancel={() => { setShowSupModal(false); setNewSupName(''); }}
        okText="Create" okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
          Quick-add a supplier name now. Add email, VAT, and payment terms later from Suppliers.
        </p>
        <Input value={newSupName} onChange={e => setNewSupName(e.target.value)}
          placeholder="Supplier name" onPressEnter={createSupplier} size="large" autoFocus />
      </Modal>

      <Modal title="New Brand" open={showBrandModal}
        onOk={createBrand} onCancel={() => { setShowBrandModal(false); setNewBrandName(''); }}
        okText="Add" okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}>
        <Input value={newBrandName} onChange={e => setNewBrandName(e.target.value)}
          placeholder="Brand / Manufacturer name" onPressEnter={createBrand} size="large" autoFocus />
      </Modal>

    </div>
  );
};

export default ProductForm;
